import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Globe, Menu, X, User, LogOut, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import gaiusLogo from '@/assets/gaius-logo.webp';

export const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage, t } = useLanguage();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAdmin } = useAdmin();

  const trainingPath = language === 'fr' ? '/formation-ia-avocat' : '/legal-ai-training-lawyers';
  const managementPath = language === 'fr' ? '/formation-ia-direction-generale' : '/ai-training-general-management';

  const navItems = [
    { key: 'home' as const, path: '/' },
    { key: 'training' as const, path: trainingPath },
    { key: 'management' as const, path: managementPath },
    { key: 'audit' as const, path: '/audit' },
    { key: 'blog' as const, path: '/blog' },
    { key: 'contact' as const, path: '/contact' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <img 
              src={gaiusLogo} 
              alt="Gaius - Formation IA avocat juriste"
              width={80}
              height={80}
              decoding="async"
              className="h-14 lg:h-20 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                className={`text-sm font-medium transition-all duration-200 hover:text-primary ${
                  isActive(item.path)
                    ? 'text-primary'
                    : 'text-muted-foreground'
                }`}
              >
                {t(item.key)}
                {isActive(item.path) && (
                  <span className="block h-0.5 gradient-bg mt-1 rounded-full" />
                )}
              </Link>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-3">
            {/* Language switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center space-x-1.5">
                  <Globe className="w-4 h-4" />
                  <span className="uppercase text-xs font-medium">{language}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-card backdrop-blur-xl">
                <DropdownMenuItem
                  onClick={() => setLanguage('en')}
                  className={`cursor-pointer ${language === 'en' ? 'bg-secondary' : ''}`}
                >
                  <span className="mr-2">🇬🇧</span>
                  {t('switchToEnglish')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setLanguage('fr')}
                  className={`cursor-pointer ${language === 'fr' ? 'bg-secondary' : ''}`}
                >
                  <span className="mr-2">🇫🇷</span>
                  {t('switchToFrench')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu or Login button */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center space-x-2 border-primary/20">
                    <User className="w-4 h-4" />
                    <span className="hidden sm:inline text-xs">
                      {user.email?.split('@')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-card backdrop-blur-xl">
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link to="/dashboard" className="flex items-center">
                      <User className="w-4 h-4 mr-2" />
                      {t('dashboard')}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/admin" className="flex items-center">
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Backoffice Admin
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-foreground">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild variant="outline" size="sm" className="hidden lg:inline-flex border-primary/20">
                <Link to="/auth">{t('login')}</Link>
              </Button>
            )}

            {/* Desktop CTA */}
            <Button asChild className="hidden lg:inline-flex gradient-bg text-primary-foreground hover:opacity-90 font-semibold">
              <Link to="/contact">{t('contact')}</Link>
            </Button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-border/50 mt-2 pt-4">
            <div className="flex flex-col space-y-3">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-sm font-medium py-2 px-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-secondary'
                  }`}
                >
                  {t(item.key)}
                </Link>
              ))}
              
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm font-medium py-2 px-3 rounded-lg text-muted-foreground hover:bg-secondary flex items-center"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {t('dashboard')}
                  </Link>
                  <button
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="text-sm font-medium py-2 px-3 rounded-lg text-foreground hover:bg-secondary flex items-center text-left"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </button>
                </>
              ) : (
                <Button asChild variant="outline" className="mt-2">
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    {t('login')}
                  </Link>
                </Button>
              )}
              
              <Button asChild className="mt-2 gradient-bg text-primary-foreground">
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  {t('contact')}
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
